import { pauseTracking, resetTracking } from "@vue/reactivity";
import axios, { AxiosError } from "axios";
import { type Ref, toValue } from "vue";

import { components as OpenAPI } from "@/autogen/openapi";
import Urls from "@/autogen/urls";
import { Newsletter } from "@/types/newsletter";
import { Context } from "@/types/preview";

const debounce = function <U>(
  func: (...args: any[]) => Promise<U> | U,
  wait: number,
  immediate: boolean = false
): (...args: any[]) => void {
  var timeout: ReturnType<typeof setTimeout> | null;

  return function executedFunction() {
    // @ts-ignore
    var context = <any>this;
    var args = arguments;

    var callNow = immediate && !timeout;

    var later = function () {
      timeout = null;
      if (!callNow) func.apply(context, Array.from(args));
    };

    if (timeout) {
      // @ts-ignore
      clearTimeout(timeout);
    }

    timeout = setTimeout(later, wait);

    if (callNow) func.apply(context, Array.from(args));
  };
};

const throttle = <P extends any[]>(
  func: (...args: P) => void,
  wait: number,
  leading = true
) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  let lastArgs: P | null = null;
  let lastCallTime = 0;

  const invoke = () => {
    const args = lastArgs!;
    lastArgs = null;
    timeout = null;

    if (leading) {
      lastCallTime = Date.now();
    }

    func(...args);
  };

  const throttled = (...args: P) => {
    lastArgs = args;

    if (leading) {
      const now = Date.now();
      const timeSinceLastCall = now - lastCallTime;

      if (timeSinceLastCall >= wait) {
        if (timeout !== null) {
          clearTimeout(timeout);
        }

        invoke();
      } else if (timeout === null) {
        timeout = setTimeout(invoke, wait - timeSinceLastCall);
      }
    } else {
      if (timeout === null) {
        timeout = setTimeout(invoke, wait);
      }
    }
  };

  throttled.now = (...args: P) => {
    if (timeout !== null) {
      clearTimeout(timeout);
    }

    lastArgs = args;
    invoke();
  };
  throttled.abort = () => {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return throttled;
};

export type RenderedMarkdown = Record<Context, string> & { error?: string };

// Needs to map to the keys of `TRANSACTIONAL_EMAIL_KEY_TO_TRANSACTIONAL_EMAIL`.
export type TransactionalEmail =
  | "confirmation"
  | "gift"
  | "premium_welcome"
  | "automation_notification"
  | "reminder";

const asynchronouslyRenderMarkdown = async (
  subject: string,
  text: string,
  id?: string,
  newsletter?: Newsletter,
  template?: OpenAPI["schemas"]["NewsletterEmailTemplate"],
  transactionalEmail?: TransactionalEmail,
  externalFeedId?: string,
  automationId?: string,
  context?: Record<string, string>
): Promise<RenderedMarkdown> => {
  try {
    const result = await axios.post(Urls["render"](), {
      text,
      subject,
      id,
      newsletter,
      template,
      transactional_email: transactionalEmail,
      external_feed_id: externalFeedId,
      automation_id: automationId,
      context,
    });
    return {
      email: result.data.result,
      email_free: result.data.result_free,
      web: result.data.web,
      web_free: result.data.web_free,
    };
  } catch (error: any) {
    return {
      email: "",
      email_free: "",
      web: "",
      web_free: "",
      error: (error as AxiosError).response?.data.message || "Unknown error",
    };
  }
};

const asynchronouslyRenderSubPage = async (
  newsletter: Newsletter
): Promise<RenderedMarkdown> => {
  try {
    const result = await axios.post(Urls["render-subscribe-template"](), {
      newsletter,
    });
    return {
      web: result.data.result,
      email: "",
      email_free: "",
      web_free: "",
    };
  } catch (error: any) {
    return {
      web: "",
      email: "",
      email_free: "",
      web_free: "",
      error: error.response ? error.response.data.message : "Unknown error",
    };
  }
};

// https://stackoverflow.com/questions/8667070/javascript-regular-expression-to-validate-url
const VALID_HREF_REGEXES = [
  /mailto:([^]*)/,
  /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i,
  /{{.*}}/,
];
const isValidHref = (url: string): boolean =>
  VALID_HREF_REGEXES.filter((r) => r.test(url)).length > 0;

// Given a hex string, e.g. #0069FF
const getTextColorForBackground = (hex: string) => {
  // Remove the #, leaving you with 0069FF.
  const rawHex = hex.replace("#", "");

  // Each two characters represents a color channel in the RGB space:
  // - R = 00
  // - G = 69
  // - B = FF
  const parseHex = (startPosition: number) =>
    parseInt(rawHex.substr(startPosition, 2), 16);

  // So we extract those channels.
  const [r, g, b] = [0, 2, 4].map(parseHex);

  // Convert theme using a YIQ formula:
  // https://en.wikipedia.org/wiki/YIQ#From_RGB_to_YIQ
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  // YIQ space is between 0 and 255:
  // If its above average (> 128), then it's light, so we want dark text.
  // If its below average (< 128), then it's dark, so we want light text.
  return yiq >= 128 ? "black" : "white";
};

// https://stackoverflow.com/a/2117523
const uuid = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const fetchCookie = (key: string): string | null => {
  return document.cookie.split(/; */).reduce((obj, str) => {
    if (str === "") return obj;
    const eq = str.indexOf("=");
    const key = eq > 0 ? str.slice(0, eq) : str;
    let val = eq > 0 ? str.slice(eq + 1) : null;
    if (val != null)
      try {
        val = decodeURIComponent(val);
      } catch {
        /* pass */
      }
    obj[key] = val;
    return obj;
  }, {} as { [key: string]: string | null })[key];
};

const diffTwoObjects = <A>(a: A, b: A, relevantKeys: (keyof A)[]) => {
  const diff: {
    [key in keyof A]?: A[key];
  } = {};
  relevantKeys.forEach((key) => {
    if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) {
      diff[key] = b[key];
    }
  });
  return diff;
};

/**
 * Checks if an array contains at least one valid non-empty string.
 * @example
 * containsValidString(["", ""]); // false
 * containsValidString([""]); // false
 * containsValidString(["", "valid"]); // true
 */
const containsValidString = (arr: string[]): boolean => {
  return arr.some(
    (answer) => typeof answer === "string" && answer.trim() !== ""
  );
};

/**
 * Removes empty values from an array of strings.
 * @example
 * removeEmptyValuesFromArray(["", "hello", "", "world"]); // Output: ["hello", "world"]
 */
const removeEmptyValuesFromArray = (arr: string[]): string[] => {
  return arr.filter((value) => value.trim() !== "");
};

/**
 * returns an array with elements unique by a selector's return value
 * @param array the array to process
 * @param selector function to derive uniqueness keys
 * @returns a new array with elements unique based on the selector's key
 */
const uniqueBy = <T, K>(
  array: T[],
  selector: (value: T, index: number) => K
): T[] => {
  const keys = new Set<K>();
  const values: T[] = [];

  for (let idx = 0, len = array.length; idx < len; idx++) {
    const value = array[idx];
    const key = selector(value, idx);

    if (keys.has(key)) {
      continue;
    }

    keys.add(key);
    values.push(value);
  }

  return values;
};

/**
 * get a random integer between min (inclusive) and max (inclusive)
 * @param min minimum value
 * @param max maximum value
 * @returns random integer
 */
const getRandomIntInclusive = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};

/**
 * grabs a random sample of items
 * @param arr array of items to sample from
 * @param n amount of items to sample
 * @returns sample
 */
const sample = <T>(arr: T[], n: number): T[] => {
  const result = [...arr];
  const len = result.length;

  n = Math.min(n, len);

  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(Math.random() * (len - i));
    [result[i], result[j]] = [result[j], result[i]];
  }

  return len > n ? result.slice(0, Math.max(0, n)) : result;
};

/*
 * access ref values without marking the ref as a watcher dependency
 * @param refOrGetter ref or getter accessing a ref
 * @returns the value of ref
 */
const untrack: {
  <T>(ref: Ref<T>): T;
  <T>(getter: () => T): T;
} = (refOrGetter: Ref<any> | (() => any)): any => {
  try {
    pauseTracking();
    return toValue(refOrGetter);
  } finally {
    resetTracking();
  }
};

export {
  asynchronouslyRenderMarkdown,
  asynchronouslyRenderSubPage,
  containsValidString,
  debounce,
  diffTwoObjects,
  fetchCookie,
  getRandomIntInclusive,
  getTextColorForBackground,
  isValidHref,
  removeEmptyValuesFromArray,
  sample,
  throttle,
  uniqueBy,
  untrack,
  uuid,
};
